import { useState, useRef } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle2, FileText } from 'lucide-react';
import { parseBudgetForecastCSV, downloadTemplate, BudgetForecastLine } from '../utils/budgetForecastCsvParser';

interface BudgetForecastImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: BudgetForecastLine[]) => void;
}

export function BudgetForecastImportModal({ isOpen, onClose, onImport }: BudgetForecastImportModalProps) {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<{ success: boolean; data?: BudgetForecastLine[]; errors?: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setParseResult({
        success: false,
        errors: ['Le fichier doit être au format CSV']
      });
      return;
    }

    setFile(file);
    setParseResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseBudgetForecastCSV(text);
      setParseResult(result);
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (parseResult?.success && parseResult.data) {
      onImport(parseResult.data);
      handleClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    setParseResult(null);
    setDragActive(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Importer un budget prévisionnel 5 ans</h2>
            <p className="text-sm text-gray-600 mt-1">Importez vos prévisions depuis un fichier CSV</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FileText className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">Télécharger le fichier template</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Utilisez notre template CSV pour formater correctement vos données. Choisissez le format adapté à votre logiciel.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => downloadTemplate(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Download size={16} />
                    Template Excel FR (point-virgule)
                  </button>
                  <button
                    onClick={() => downloadTemplate(false)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                  >
                    <Download size={16} />
                    Template Standard (virgule)
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Format du fichier CSV</h3>
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
              <p className="text-gray-700">Le fichier CSV doit contenir les colonnes suivantes :</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                <li><span className="font-medium">Ligne budgétaire</span> : nom de la ligne</li>
                <li><span className="font-medium">Type</span> : "Recette" ou "Dépense"</li>
                <li><span className="font-medium">Année 1 à 5</span> : montants (accepte espaces, €, virgules)</li>
              </ul>
              <p className="text-xs text-gray-500 mt-3">
                Les deux séparateurs sont acceptés : virgule (,) ou point-virgule (;)
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Importer votre fichier</h3>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-700 mb-2">
                Glissez-déposez votre fichier CSV ici
              </p>
              <p className="text-sm text-gray-500 mb-4">ou</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Parcourir les fichiers
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleChange}
                className="hidden"
              />
            </div>
          </div>

          {file && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Fichier sélectionné :</span> {file.name}
              </p>
            </div>
          )}

          {parseResult && !parseResult.success && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-2">Erreurs détectées</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {parseResult.errors?.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {parseResult && parseResult.success && parseResult.data && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900 mb-2">Fichier validé avec succès</h4>
                  <p className="text-sm text-green-800">
                    {parseResult.data.length} ligne(s) budgétaire(s) prête(s) à être importée(s)
                  </p>
                  <div className="mt-3 space-y-1 text-sm text-green-800">
                    <p>
                      Recettes : {parseResult.data.filter(d => d.type === 'Recette').length} ligne(s)
                    </p>
                    <p>
                      Dépenses : {parseResult.data.filter(d => d.type === 'Dépense').length} ligne(s)
                    </p>
                  </div>

                  <details className="mt-3">
                    <summary className="text-sm font-medium text-green-900 cursor-pointer hover:text-green-700">
                      Voir le détail des lignes
                    </summary>
                    <div className="mt-2 max-h-60 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-green-100">
                          <tr>
                            <th className="text-left p-2">Nom</th>
                            <th className="text-left p-2">Type</th>
                            <th className="text-right p-2">Année 1</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseResult.data.map((line, idx) => (
                            <tr key={idx} className="border-t border-green-200">
                              <td className="p-2">{line.name}</td>
                              <td className="p-2">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  line.type === 'Recette'
                                    ? 'bg-green-200 text-green-900'
                                    : 'bg-orange-200 text-orange-900'
                                }`}>
                                  {line.type}
                                </span>
                              </td>
                              <td className="text-right p-2">{line.year1.toLocaleString('fr-FR')} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleImport}
            disabled={!parseResult?.success}
            className={`px-4 py-2 rounded-lg transition-colors ${
              parseResult?.success
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Importer {parseResult?.data?.length || 0} ligne(s)
          </button>
        </div>
      </div>
    </div>
  );
}
